import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow, PrimaryCTA, SectionNum } from "@/components/ui-bits";
import { VISIBLE_PRACTICE_AREAS } from "@/data/practiceAreas";
import { FIRM } from "@/data/firm";
import { ArrowUpRight } from "lucide-react";

// ─────────────────────────────────────────────────────────────
// Copy is drafted from patterns common to Rimon / Pierson Ferdinand /
// Culhane Meadows / FisherBroyles / Lucendo. Placeholders marked
// [[BRACKETED]] are firm-specific — replace with true values before
// promoting to Production.
// ─────────────────────────────────────────────────────────────

const PILLARS = [
  {
    num: "01",
    title: "Transparent economics",
    body:
      "A partner-payout model built for experienced attorneys who bring their own clients. No origination games, no equity buy-in, and no pyramid — partners keep a high percentage of the fees they generate, with a clear, published schedule for shared expenses.",
  },
  {
    num: "02",
    title: "Real infrastructure",
    body:
      "The firm provides the back office that most solo and small-firm attorneys do not want to build: engagement letters and conflict checks, matter management and time-capture platforms, secure document storage, malpractice coverage, IOLTA and billing, and the vendor stack used by modern firms.",
  },
  {
    num: "03",
    title: "Autonomy on the file",
    body:
      "Partners run their own matters. There are no committees between the attorney and the client, no forced staffing on unrelated work, and no artificial billing targets. The firm exists to support the practice — not to route it.",
  },
  {
    num: "04",
    title: "A defensible niche",
    body:
      "The firm is deliberately built around business, intellectual property, and the federal regulatory practices that increasingly govern them — international trade, export controls, CFIUS, government contracts, FCC, and insurance. Partners join a practice that already has a shape.",
  },
];

const FIT = [
  {
    h: "Experienced attorneys",
    p: "Partners typically join with 10+ years of substantive practice and a book of business or a strong pipeline. The firm is not a training ground.",
  },
  {
    h: "Practice areas that fit the firm",
    p: "Corporate, IP, AI, international trade, export compliance, CFIUS, government contracts, semiconductor industry, FCC, insurance — and adjacent regulatory practices. Non-adjacent practices are considered case-by-case.",
  },
  {
    h: "Portable client relationships",
    p: "Confirmed conflict-clear relationships, or the credible ability to develop them. Book size varies — quality of relationships matters more than headline revenue.",
  },
  {
    h: "Bar in good standing",
    p: "Admission in at least one U.S. state, in good standing, with no unresolved disciplinary matters or open litigation.",
  },
];

export default function Join() {
  return (
    <Layout>
      <SEO
        title="Careers"
        description="Shelton Law PLLC is building a distributed partnership for experienced attorneys. Transparent economics, real infrastructure, autonomy on the file. Introductions are confidential."
        path="/join"
      />
      {/* Hero */}
      <section className="border-b border-hairline" data-testid="join-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-24 lg:pt-32 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-1 hidden lg:block">
              <div className="rule-brass w-12 mt-4" />
            </div>
            <div className="lg:col-span-11">
              <Eyebrow>Careers</Eyebrow>
              <h1 className="display-1 text-ink mt-6 max-w-5xl">
                A better place to practice — for the attorneys who have already learned the trade.
              </h1>
              <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                <p className="lg:col-span-7 text-ink-soft text-[1.0625rem] leading-relaxed max-w-2xl">
                  Shelton Law PLLC is building a distributed partnership for experienced attorneys who want to keep their clients close, keep more of what they earn, and practice inside a firm that respects the work.
                </p>
                <div className="lg:col-span-5 lg:flex lg:items-end lg:justify-end">
                  <PrimaryCTA to="/contact" testId="join-hero-cta">Speak With Jack</PrimaryCTA>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision / founder note */}
      <section className="bg-ivory border-b border-hairline" data-testid="join-vision">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Eyebrow>The Vision</Eyebrow>
            <p className="section-num mt-1">01</p>
          </div>
          <div className="lg:col-span-8">
            <h2 className="display-2 text-ink leading-tight max-w-3xl">
              Founded to give experienced attorneys a firm worth staying at.
            </h2>
            <div className="mt-8 prose-law max-w-3xl">
              <p>
                Shelton Law was founded by Jack Shelton after a career representing companies in the maritime, defense, and commercial space industries — the kinds of matters that sit at the intersection of business and federal regulation. The firm&apos;s practice areas were chosen deliberately, not opportunistically, and the firm&apos;s structure was chosen for the attorneys inside it.
              </p>
              <p>
                The intent is straightforward: build a distributed partnership of senior attorneys who can staff a sophisticated client relationship without leaving BigLaw quality behind — and without the leverage economics, committee overhead, and origination politics that push good lawyers out of BigLaw in the first place.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Four pillars — economics / infrastructure / autonomy / niche */}
      <section className="bg-ivory-alt border-b border-hairline" data-testid="join-pillars">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-4">
              <Eyebrow>How the Firm Works</Eyebrow>
              <p className="section-num mt-1">02</p>
              <h2 className="display-2 text-ink mt-5 leading-tight">
                Four commitments.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <p className="text-ink-soft text-[1.0625rem] leading-relaxed">
                The firm is structured around four commitments to the partners inside it. Each is meant to answer a specific frustration that experienced attorneys have with the firms they leave.
              </p>
            </div>
          </div>

          <ul className="border-t border-hairline" data-testid="join-pillars-list">
            {PILLARS.map((p) => (
              <li key={p.num} className="border-b border-hairline grid grid-cols-12 gap-6 py-10 lg:py-12 items-start">
                <SectionNum className="col-span-2 sm:col-span-1 self-start pt-2">{p.num}</SectionNum>
                <div className="col-span-10 sm:col-span-4">
                  <h3 className="font-serif text-2xl sm:text-3xl text-ink leading-tight">{p.title}</h3>
                </div>
                <div className="col-span-12 sm:col-span-7 text-ink-soft text-[1.0625rem] leading-relaxed">
                  {p.body}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Landing zones — practice areas as a bench */}
      <section className="border-b border-hairline" data-testid="join-landing-zones">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Eyebrow>Landing Zones</Eyebrow>
            <p className="section-num mt-1">03</p>
            <h2 className="display-2 text-ink mt-5 leading-tight">
              Where your practice fits.
            </h2>
            <p className="mt-6 text-ink-soft text-[1.0625rem] leading-relaxed">
              The firm&apos;s existing practice areas are the primary landing zones for incoming partners. Adjacent practices — cybersecurity, privacy, tax, litigation of covered subject matter — are actively being considered.
            </p>
          </div>
          <div className="lg:col-span-8">
            <ul className="border-t border-hairline">
              {VISIBLE_PRACTICE_AREAS.map((p) => (
                <li key={p.slug} className="border-b border-hairline grid grid-cols-12 gap-4 py-5">
                  <SectionNum className="col-span-2 sm:col-span-1 self-start pt-1.5">{p.number}</SectionNum>
                  <div className="col-span-10 sm:col-span-11">
                    <h3 className="font-serif text-xl text-ink leading-tight">{p.name}</h3>
                    <p className="mt-1 text-ink-soft text-sm leading-relaxed">{p.kicker}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Fit criteria */}
      <section className="bg-ivory-alt border-b border-hairline" data-testid="join-fit">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Eyebrow>Who Fits</Eyebrow>
            <p className="section-num mt-1">04</p>
            <h2 className="display-2 text-ink mt-5 leading-tight">
              Who the firm is for.
            </h2>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
            {FIT.map((f) => (
              <div key={f.h}>
                <h3 className="font-serif text-xl text-ink leading-tight">{f.h}</h3>
                <p className="mt-2 text-ink-soft text-[0.95rem] leading-relaxed">{f.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="border-b border-hairline" data-testid="join-process">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Eyebrow>The Conversation</Eyebrow>
            <p className="section-num mt-1">05</p>
            <h2 className="display-2 text-ink mt-5 leading-tight">
              How it goes.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <ol className="border-t border-hairline">
              {[
                { h: "An introductory call", p: "Thirty minutes with Jack. Practice, book, timing, and mutual fit. Confidential." },
                { h: "Diligence, both directions", p: "You review the firm's economics, infrastructure, and current partnership. The firm reviews conflicts, admissions, and portable client relationships." },
                { h: "Terms and transition", p: "A written offer, a start date, and a transition plan for existing matters and client notifications." },
                { h: "Onboarding", p: "The firm handles infrastructure setup — email, malpractice, billing, matter files — so the first week is spent on client work, not admin." },
              ].map((s, i) => (
                <li key={s.h} className="border-b border-hairline grid grid-cols-12 gap-4 py-6 items-baseline">
                  <SectionNum className="col-span-2 sm:col-span-1 self-start pt-1.5">{String(i + 1).padStart(2, "0")}</SectionNum>
                  <div className="col-span-10 sm:col-span-11">
                    <h3 className="font-serif text-xl text-ink leading-tight">{s.h}</h3>
                    <p className="mt-1 text-ink-soft text-[0.95rem] leading-relaxed">{s.p}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-ink text-ivory" data-testid="join-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.28em] text-brass-light font-semibold mb-6">Lateral Inquiries</p>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight tracking-tight">
              Introductions are confidential and go directly to the founder.
            </h2>
            <p className="mt-6 text-ivory/70 text-[1.0625rem] leading-relaxed max-w-2xl">
              Email is the fastest route. A one-paragraph description of your practice, book, and current firm is enough for an initial call.
            </p>
          </div>
          <div className="lg:col-span-5 lg:text-right space-y-3">
            <p className="text-ivory/70 text-xs tracking-[0.2em] uppercase">Attorney Inquiries</p>
            <a href={FIRM.careersEmailHref} className="block font-serif text-3xl text-ivory hover:text-brass-light transition-colors" data-testid="join-email">
              {FIRM.careersEmail}
            </a>
            <p className="text-ivory/60 text-xs pt-3 max-w-xs lg:ml-auto">Confidential. A one-paragraph description of your practice, book, and current firm is enough for an initial call.</p>
            <div className="pt-6 flex lg:justify-end">
              <PrimaryCTA to="/contact" testId="join-cta-btn">Start the Conversation <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} /></PrimaryCTA>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
